import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { FEATURES } from './config/features';
import { initAnalytics } from './services/analytics';

// Layouts
import PublicLayout from './components/PublicLayout';
import Layout from './components/Layout';

// Pages
import Home from './pages/Home';
import Product from './pages/Product';
import Solutions from './pages/Solutions';
import Pricing from './pages/Pricing';
import Resources from './pages/Resources';
import RequestDemo from './pages/RequestDemo';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Talent from './pages/Talent';
import ForCandidates from './pages/ForCandidates';
import ForRecruiters from './pages/ForRecruiters';
import Join from './pages/Join';
import Trust from './pages/Trust';
import ManageProfile from './pages/ManageProfile';
import Refer from './pages/Refer';
import Dashboard from './pages/Dashboard'; // Reverted from AdminDashboard
import TalentPool from './pages/dashboard/TalentPool'; // Import new dashboard page
import Jobs from './pages/Jobs';
import Candidates from './pages/Candidates';
import Outreach from './pages/Outreach';
import JobMatching from './pages/JobMatching';
import Account from './pages/Account';
import Autopilot from './pages/Autopilot';
import AutoApply from './pages/AutoApply';
import Pool from './pages/Pool';
import RoleMatches from './pages/RoleMatches';
import Analytics from './pages/Analytics';
import Calendar from './pages/Calendar';
import Integrations from './pages/Integrations';
import Recommendations from './pages/Recommendations';
import Pipeline from './pages/Pipeline';
import SavedSearches from './pages/SavedSearches';
import Collaboration from './pages/Collaboration';
import Feedback from './pages/Feedback';
import ProofScore from './pages/ProofScore';
import ProofComplete from './pages/ProofComplete';
import ProofAuthor from './pages/ProofAuthor';
import MLStudy from './pages/MLStudy';
import Status from './pages/Status';

// Components
import ProtectedRoute from './components/ProtectedRoute';

import { ThemeProvider } from './context/ThemeContext';
import { SupabaseAuthProvider } from './contexts/SupabaseAuthContext';

function App() {
  initAnalytics();
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <SupabaseAuthProvider>
        <ThemeProvider>
          <Router>
          <ToastContainer position="top-right" autoClose={3000} />
          <Routes>
            {/* Public Routes */}
            {/* Status Page - Public, no layout */}
            <Route path="/status" element={<Status />} />

            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/product" element={<Product />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/request-demo" element={<RequestDemo />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/talent" element={<Talent />} />
              {/* Two-sided routing: dedicated audience landing pages + candidate join */}
              <Route path="/for-candidates" element={<ForCandidates />} />
              <Route path="/for-recruiters" element={<ForRecruiters />} />
              <Route path="/join" element={<Join />} />
              <Route path="/trust" element={<Trust />} />
              <Route path="/profile/manage" element={<ManageProfile />} />
            </Route>

            {/* Protected Routes */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/app/talent-pool" element={<TalentPool />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/candidates" element={<Candidates />} />
              <Route path="/outreach" element={<Outreach />} />
              <Route path="/job-matching" element={<JobMatching />} />
              {FEATURES.autopilot && <Route path="/autopilot" element={<Autopilot />} />}
              <Route path="/apply" element={<AutoApply />} />
              <Route path="/pool" element={<Pool />} />
              <Route path="/pool/roles" element={<RoleMatches />} />
              <Route path="/refer" element={<Refer />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/dashboard/recommendations" element={<Recommendations />} />
              <Route path="/dashboard/pipeline" element={<Pipeline />} />
              <Route path="/saved-searches" element={<SavedSearches />} />
              <Route path="/collaboration" element={<Collaboration />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/proof" element={<ProofScore />} />
              <Route path="/proof/author" element={<ProofAuthor />} />
              <Route path="/proof/scores/:id" element={<ProofScore />} />
              <Route path="/ml-study" element={<MLStudy />} />
              <Route path="/account" element={<Account />} />
            </Route>

            {/* Candidate work-sample (full-screen, no recruiter chrome) */}
            <Route path="/proof/complete/:id" element={<ProtectedRoute><ProofComplete /></ProtectedRoute>} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Router>
        </ThemeProvider>
      </SupabaseAuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App; 